import type { StudentLessonsProps, StudentStateType, StudentType, StudentTypeWithReassessment, YourStudentsRecentStateType, YourStudentsStateType } from '../pages/types';
import type { GroupReportInterface, NewStudentBasicInterface, NewStudentTutorDataInterface } from '@whizz/api-model';
import { repository } from '@whizz/api-client';
import { DateTime, Interval } from 'luxon';
import type NewStudentBasic from '@whizz/api-client/dist/@types/model/tx3/NewStudentBasic';
import type NewStudentTutorData from '@whizz/api-client/dist/@types/model/tx3/NewStudentTutorData';
import { round } from '../util/numbers';
import { convertYearsToWeeks } from '../util/dates';

export const mapAllGroupReportPosNeg =
  (groupReport: GroupReportInterface): { positive: StudentType[], negative: StudentType[] } => {
    return {
      positive: mapGroupReportStudents(repository.GroupReportRepository.getStudentsGoingWell(groupReport), 'started'),
      negative: [
        ...mapGroupReportStudents(repository.GroupReportRepository.getStudentsWithNoLogin(groupReport), 'no_logged'),
        ...mapGroupReportStudents(repository.GroupReportRepository.getStudentsWithNoAssessment(groupReport), 'no_assessment'),
        ...mapGroupReportStudents(repository.GroupReportRepository.getStudentsWithNoProgression(groupReport), 'no_progression'),
        ...mapGroupReportStudents(repository.GroupReportRepository.getStudentsInReassessment(groupReport), 'in_reassessment'),
      ],
    };
  };

const mapGroupReportStudents = (groupReportStudents: NewStudentTutorDataInterface[], state: StudentStateType): StudentType[] => {
  return groupReportStudents.map((groupReportStudent) => {
    return {
      ...mapGroupReportMinimalStudent(groupReportStudent),
      state: state,
      maths_age: round(groupReportStudent.mathsAge, 2),
      haveNotCompletedRelevantDate: groupReportStudent.haveNotCompletedRelevantDate,
      progress: groupReportStudent.percentageCompletedLastAssessment,
    };
  });
};

const mapGroupReportMinimalStudent = (groupReportStudent: (NewStudentTutorDataInterface | NewStudentBasicInterface)): StudentType => {
  const student: StudentType = {
    id: groupReportStudent.id,
    fullName: `${groupReportStudent.firstName} ${groupReportStudent.lastName}`,
    firstName: groupReportStudent.firstName,
    loginDetails: {
      username: groupReportStudent.username,
    },
  };

  if ('gender' in groupReportStudent) {
    student.gender = groupReportStudent.gender;
  }

  return student;
};

export const mapAllGroupReportForDropdown = (groupReport: GroupReportInterface): StudentType[] => {
  return groupReport.students.map(mapGroupReportMinimalStudent);
};

export const mapGroupReportForLastWeeks = (groupReport: GroupReportInterface): StudentTypeWithReassessment[] => {
  return groupReport.students.map((groupReportStudent) => {
    const maths_age = round(groupReportStudent.mathsAge, 2);

    const diff = Interval.fromDateTimes(groupReport.startDate as DateTime, groupReport.endDate as DateTime);
    const diffWeeks = diff.length('weeks');

    return {
      ...mapGroupReportMinimalStudent(groupReportStudent),
      maths_age: maths_age,
      hasBeenReassessed: groupReportStudent.hasBeenReassessed,
      reassessmentEndDate: groupReportStudent.reassessmentEndDate,
      chartsData: {
        mathsAge: maths_age,
        mathsAgeImprovement: convertYearsToWeeks(groupReportStudent.weeksMathsAgeDelta),
        progression: round((groupReportStudent.weeksProgression / diffWeeks), 2),
        qualityLearningTime: groupReportStudent.weeksLearningTime,
      },
    };
  });
};

export const mapGroupReportForLastMonday = (groupReport: GroupReportInterface): StudentType[] => {
  return groupReport.students.map((groupReportStudent) => {
    const maths_age = round(groupReportStudent.mathsAge, 2);
    const studentStates = getStudentStates(groupReportStudent, groupReport.recentDate);
    return {
      ...mapGroupReportMinimalStudent(groupReportStudent),
      lastLogonDate: groupReportStudent.lastLogonDate,
      yourStudentsStates: studentStates,
      maths_age: maths_age,
      chartsData: {
        mathsAge: maths_age,
        mathsAgeImprovement: 0,
        progression: round(groupReportStudent.weekProgression, 1),
        qualityLearningTime: round(groupReportStudent.weekLearningTime, 0),
      },
    };
  });
};

export const mapGroupReportForYourStudents = (groupReport: GroupReportInterface): StudentType[] => {
  return groupReport.students.map((student) => {
    const studentStates = getStudentStates(student, groupReport.recentDate);

    const studentLessonDetails: StudentLessonsProps = {};
    studentStates.forEach((state) => {
      if (![ 'failed_2' ].includes(state)) {
        return;
      }

      const lesson = repository.NewStudentTutorDataRepository.getStuckLesson(student);

      studentLessonDetails[state] = {
        topic: lesson.topic,
        lesson: lesson.title,
        lessonId: lesson.lessonID,
      };
    });

    return {
      ...mapGroupReportMinimalStudent(student),
      yourStudentsStates: studentStates,
      lessonsDetails: studentLessonDetails, // To show in multiple sub sections
      progress: student.percentageCompletedLastAssessment, // To show in `being reassessed`
      chartsData: {
        mathsAge: 0,
        mathsAgeImprovement: 0,
        progression: student.weekProgression, // To show in `Great Job`
        qualityLearningTime: student.weekLearningTime, // To show in `Great Job`
      },
    };
  });
};

// TODO: Move to the repository(?)
const getStudentStates = (originalStudent: NewStudentTutorDataInterface, recentDate: GroupReportInterface['recentDate']): (YourStudentsStateType[] & YourStudentsRecentStateType[]) => {
  // haven't logged in
  const student = {
    ...originalStudent,
    lastLessonOutcomes: originalStudent.lastLessonOutcomes.map((lesson) => lesson) || [],
  };
  const lastLogonDate = student.lastLogonDate as DateTime | null;
  const formattedRecentDate = (recentDate instanceof DateTime) ? recentDate : DateTime.fromISO(recentDate);
  const noLogin = null == lastLogonDate || lastLogonDate < formattedRecentDate;

  if (student.isBeingReassessed) {
    return [ 'reassessed', noLogin ? 'no_logging' : false ].filter(Boolean) as string[];
  }

  // Great Job
  // TODO: Is weekLearningTime coming in seconds, minutes?
  // TODO: Nic / David - new requirement on the new endpoing regarding "good news" / "bad news"
  const greatJob = student.weekProgression >= 3;
  //Good usage, but low progressions
  const goodUsageLowProgressions = student.weekLearningTime >= 30 && student.weekProgression < 3;

  // Progressing slowly
  const slowly = repository.NewStudentTutorDataRepository.getProgressingSlowlyLessons(student).length > 0;

  // stuck
  // Applied to completed lessons
  const stuck = repository.NewStudentTutorDataRepository.isStuck(student);

  // struggling
  const struggling = repository.NewStudentTutorDataRepository.isStruggling(student);

  return [
    greatJob ? 'great_job' : false,
    slowly ? 'slow_progress' : false,
    stuck ? 'failed_2' : false,
    goodUsageLowProgressions ? 'good_usage_low_progressions' : false,
    struggling ? 'struggling' : false,
    noLogin ? 'no_logging' : false,
  ].filter(Boolean) as string[];
};

export const mapGroupBasicStudents = (studentsBasic: NewStudentBasicInterface[]): StudentType[] => {
  return studentsBasic.map(mapGroupReportMinimalStudent);
};

/* Remove after migrating last items to repository/interfaces */

export const mapGroupBasicStudentsObjects = (studentsBasic: NewStudentBasic[]): StudentType[] => {
  return studentsBasic.map(mapGroupReportMinimalStudentObject);
};

const mapGroupReportMinimalStudentObject = (groupReportStudent: (NewStudentTutorData | NewStudentBasic)): StudentType => {
  const student: StudentType = {
    id: groupReportStudent.getId(),
    fullName: groupReportStudent.getName('FULL'),
    firstName: groupReportStudent.getName('FIRST'),
  };

  if ('getGender' in groupReportStudent) {
    student.gender = groupReportStudent.getGender();
  }

  return student;
};
